import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
    approveReview,
    fetchExtensions,
    fetchJourney,
    fetchKatlasStatus,
    refuseReview,
} from "@/lib/api";
import Hero from "@/components/Hero";
import ExplainerCards from "@/components/ExplainerCards";
import JourneyStrip from "@/components/JourneyStrip";
import PatientWallet from "@/components/PatientWallet";
import AiSignal from "@/components/AiSignal";
import ConsentRequest from "@/components/ConsentRequest";
import EvidenceBoundary from "@/components/EvidenceBoundary";
import AuthorityGate from "@/components/AuthorityGate";
import ReceiptPanel from "@/components/ReceiptPanel";
import ResponsibleAiBoundary from "@/components/ResponsibleAiBoundary";
import SafeExchange from "@/components/SafeExchange";
import HealthCoach from "@/components/HealthCoach";
import SensitiveDisclosure from "@/components/SensitiveDisclosure";
import CarerAuthority from "@/components/CarerAuthority";
import EndOfLifeCertificates from "@/components/EndOfLifeCertificates";
import CriticalFindingScenario from "@/components/CriticalFindingScenario";

export default function HealthCierge() {
    const [katlas, setKatlas] = useState({ status: "loading", label: "" });
    const [journey, setJourney] = useState(null);
    const [extensions, setExtensions] = useState(null);
    const [busy, setBusy] = useState(false);
    const [actionResponse, setActionResponse] = useState(null);

    useEffect(() => {
        fetchKatlasStatus()
            .then(setKatlas)
            .catch(() =>
                setKatlas({
                    status: "unreachable",
                    label: "KATLAS status unavailable",
                })
            );
        fetchJourney()
            .then(setJourney)
            .catch((e) => {
                console.error(e);
                toast.error("Failed to load current journey");
            });
        fetchExtensions()
            .then(setExtensions)
            .catch((e) => {
                console.error(e);
            });
    }, []);

    const activeStep = useMemo(() => {
        if (!actionResponse) return 2; // through review
        return 4;
    }, [actionResponse]);

    const runAction = async (kind, actor) => {
        setBusy(true);
        try {
            const fn = kind === "approve" ? approveReview : refuseReview;
            const res = await fn({ actor_role: actor });
            setActionResponse(res);
            if (res.ok) {
                toast.success(
                    `Cardiovascular review route ${
                        kind === "approve" ? "approved" : "refused"
                    } · KATLAS-signed`
                );
            } else {
                toast(
                    `Action recorded · KATLAS ${res.katlas?.status || "unconfigured"}`,
                    {
                        description:
                            "No simulated receipt produced (honest unconfigured state).",
                    }
                );
            }
            setTimeout(() => {
                document
                    .getElementById("receipt-anchor")
                    ?.scrollIntoView({ behavior: "smooth", block: "start" });
            }, 80);
        } catch (e) {
            console.error(e);
            toast.error("Action failed");
        } finally {
            setBusy(false);
        }
    };

    return (
        <main className="min-h-screen" style={{ background: "var(--bone)" }}>
            <Hero katlas={katlas} />
            <ExplainerCards />
            <JourneyStrip active={activeStep} />

            <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-16 space-y-12">
                <PatientWallet wallet={journey?.patient_wallet} />
                <AiSignal signal={journey?.ai_support_signal} />
                <ConsentRequest request={journey?.consent_request} />
                <EvidenceBoundary
                    shared={journey?.evidence_shared || []}
                    withheld={journey?.evidence_withheld || []}
                />
                <AuthorityGate
                    roles={journey?.acting_roles || []}
                    onApprove={(actor) => runAction("approve", actor)}
                    onRefuse={(actor) => runAction("refuse", actor)}
                    busy={busy}
                    katlasStatus={katlas?.status}
                />
                <div id="receipt-anchor">
                    {actionResponse && <ReceiptPanel response={actionResponse} />}
                </div>
                <ResponsibleAiBoundary />
                <SafeExchange />
                <HealthCoach />
            </div>

            <ExtensionsHeading />
            <div
                className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 pb-16 space-y-12"
                data-testid="governance-extensions"
            >
                <SensitiveDisclosure data={extensions?.sensitive_disclosure} />
                <CarerAuthority data={extensions?.carer_authority} />
                <EndOfLifeCertificates data={extensions?.eol_certificates} />
                <CriticalFindingScenario
                    scenario={extensions?.critical_finding_scenario}
                    katlasStatus={katlas?.status}
                />
            </div>

            <Footer />
        </main>
    );
}

const ExtensionsHeading = () => (
    <section className="border-t hairline bg-[var(--paper-alt)]">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-12">
            <p className="mono-label">Governance extensions · optional</p>
            <h2
                className="font-serif mt-2"
                style={{
                    fontSize: "clamp(28px, 3.6vw, 44px)",
                    letterSpacing: "-0.015em",
                    lineHeight: 1.05,
                    maxWidth: 880,
                }}
            >
                Bounded safety scenarios layered on the primary cardiovascular
                route — sensitive disclosure, carer authority and care-preference
                certificates.
            </h2>
            <p
                className="mt-4 font-serif"
                style={{
                    fontSize: 15.5,
                    color: "var(--ink-soft)",
                    maxWidth: 720,
                }}
            >
                Primary v1 remains: wallet signal → consent request → authorised
                review → KATLAS-signed receipt. The panels below show how
                HealthCierge governs harder safety cases without changing that
                core route.
            </p>
        </div>
    </section>
);

const Footer = () => (
    <footer className="border-t hairline" data-testid="footer">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-10 grid grid-cols-1 md:grid-cols-3 gap-8">
            <div>
                <p className="mono-label">HealthCierge</p>
                <p
                    className="mt-2 font-serif"
                    style={{ fontSize: 14, color: "var(--ink-soft)" }}
                >
                    KATLAS records the governed action — not a copy of everyone's
                    private data. Evidence stays at source.
                </p>
            </div>
            <div>
                <p className="mono-label">CAR</p>
                <p className="mt-2" style={{ fontSize: 13.5 }}>
                    Custody controls the evidence. Authority governs the action.
                    Receipts prove what happened.
                </p>
            </div>
            <div>
                <p className="mono-label">Demonstrator scope</p>
                <p
                    className="mt-2"
                    style={{ fontSize: 13.5, color: "var(--ink-soft)" }}
                >
                    Not a clinical product, diagnostic engine or medical device. A
                    governance demonstrator only.
                </p>
            </div>
        </div>
    </footer>
);
